import { motion } from "framer-motion";
import SectionLabel from "../components/SectionLabel";
import GoldButton from "../components/GoldButton";
import { BRAND } from "../data/content";

/**
 * Reusable placeholder page for Phase 1 stubs
 * (Portfolio, Process, About, Insights, Contact).
 * Shows full nav + footer with tasteful "Coming soon" messaging.
 */
export default function StubPage({
    eyebrow,
    headline,
    body,
    showContact = false,
    testId,
}) {
    return (
        <div
            className="min-h-[72vh] flex flex-col bg-[#0B0B0C]"
            data-testid={testId}
        >
            <section className="relative flex-1 flex items-center overflow-hidden">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0"
                >
                    <div className="absolute -top-40 -left-40 h-[600px] w-[600px] max-w-[140vw] rounded-full bg-[#C9A86A]/5 blur-[140px]" />
                    <div className="absolute -bottom-40 -right-40 h-[600px] w-[600px] max-w-[140vw] rounded-full bg-[#C9A86A]/5 blur-[140px]" />
                </div>

                <div className="relative shell py-24 md:py-32 lg:py-40">
                    <motion.div
                        suppressHydrationWarning
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 0.9,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className="max-w-3xl"
                    >
                        <SectionLabel number="—" label={eyebrow} />

                        <h1 className="mt-8 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
                            {headline}
                        </h1>

                        <p className="mt-10 text-[#9A958A] text-base md:text-lg leading-[1.9] max-w-xl">
                            {body}
                        </p>

                        {showContact && (
                            <div className="mt-14 space-y-4 border-t border-[rgba(201,168,106,0.2)] pt-10 max-w-lg">
                                <div className="flex flex-col sm:flex-row gap-8">
                                    <div>
                                        <span className="eyebrow">Telephone</span>
                                        <a
                                            href={BRAND.phoneHref}
                                            className="mt-2 block font-serif text-3xl md:text-4xl text-[#F3EFE7] hover:text-[#C9A86A] transition-colors"
                                            data-testid="stub-contact-phone"
                                        >
                                            {BRAND.phone}
                                        </a>
                                    </div>
                                    <div>
                                        <span className="eyebrow">Concierge</span>
                                        <a
                                            href={BRAND.emailHref}
                                            className="mt-2 block text-[#F3EFE7] hover:text-[#C9A86A] transition-colors break-all"
                                            data-testid="stub-contact-email"
                                        >
                                            {BRAND.email}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mt-14 flex flex-col sm:flex-row gap-4">
                            <GoldButton
                                to="/"
                                variant="outline"
                                testId="stub-return-home"
                            >
                                Return Home
                            </GoldButton>
                            {!showContact && (
                                <GoldButton
                                    to="/contact"
                                    testId="stub-contact-cta"
                                >
                                    Speak with the Concierge
                                </GoldButton>
                            )}
                        </div>

                        <p className="mt-16 text-[11px] tracking-[0.32em] uppercase text-[#9A958A]">
                            Forthcoming · Phase 2
                        </p>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
